import { getSelectStyles, getActionSelectStyles } from '../utility/selectStyles';
import React, { useState, useEffect } from 'react';
import { FaPlus, FaSyncAlt, FaTrash } from 'react-icons/fa';
import InfoBanner from '../components/InfoBanner';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import { fetchClusters, Cluster } from '../store/slices/clustersSlice';
import { fetchImages } from '../store/slices/imagesSlice';
import { fetchFlavors } from '../store/slices/flavorsSlice';
import { fetchPrivateNetwork } from '../store/slices/privateNetworkSlice';
import { fetchKeypairs } from '../store/slices/keypairsSlice';
import Select from 'react-select';
import axiosInstance from "../utility/axiosInstance";
import { useToast } from "../components/ToastContext";
import { useConfirm } from "../components/ConfirmContext";
import CreateClusterModal from '../components/CreateClusterModal';
import UpdateClusterModal from '../components/UpdateClusterModal';
import ClusterDetailsModal from '../components/ClusterDetailsModal';
import RollingUpdateModal from '../components/RollingUpdateModal';
import TrialLock from '../components/TrialLock';
import { fetchSnapshots } from '../store/slices/snapshotsSlice';
import useAutoOpenCreateModal from '../hooks/useAutoOpenCreateModal';
import { formatConsoleDate } from '../utility/formatConsoleDate';

import '../styles/InstancesPage.scss';

const ITEMS_PER_PAGE = 10;

/**
 * "BUILDING 1 OF 3" while an HA cluster ramps.
 *
 * A 3-member cluster comes up one member at a time (creating them together defeats the anti-affinity
 * spread), so "CREATING" would sit unchanged for several minutes with no sign of progress. Returns
 * null when the backend sent no target - single-member clusters, and clusters created before this
 * shipped - and the caller falls back to the plain status word.
 */
function buildProgressLabel(cluster: Cluster): string | null {
    const target = cluster.target_members;
    if (!target || target < 2) return null;
    const ready = cluster.members_ready;
    // The target tag is permanent, so it outlives the build. Without this, every later operation on
    // the cluster (a rolling update, a config change) would read "BUILDING 3 OF 3" instead of
    // UPDATING. Showing it only while members are actually missing keeps it truthful in both cases:
    // during a roll it correctly reads "BUILDING 2 OF 3" as each member is replaced.
    if (ready === undefined || ready >= target) return null;
    return `BUILDING ${ready} OF ${target}`;
}

const ClustersPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { showToast } = useToast();
    const { confirm } = useConfirm();
    
    const clusters = useSelector((state: RootState) => state.clusters.data);
    const status = useSelector((state: RootState) => state.clusters.status);
    
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedCluster, setSelectedCluster] = useState<any>(null);
    const [isRollingUpdateModalOpen, setIsRollingUpdateModalOpen] = useState(false);
    const [deletingClusters, setDeletingClusters] = useState<Set<string>>(new Set());
    const [currentPage, setCurrentPage] = useState(0);

    const creationLocked = useAutoOpenCreateModal(setIsCreateModalOpen);

    useEffect(() => {
        dispatch(fetchClusters());
        dispatch(fetchImages());
        dispatch(fetchFlavors());
        dispatch(fetchPrivateNetwork());
        dispatch(fetchKeypairs());
        dispatch(fetchSnapshots());
    }, [dispatch]);

    const handleDeleteCluster = async (stackName: string, stackId: string) => {
        const proceed = await confirm({
            title: 'Delete Cluster',
            message: `Are you sure you want to delete cluster "${stackName}"? This will remove all associated instances, load balancers, and security groups.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
        });
        if (!proceed) return;

        setDeletingClusters(prev => new Set(prev).add(stackId));
        try {
            await axiosInstance.delete(`/v1/clusters/${stackName}/${stackId}`);
            showToast('Cluster deletion initiated', 'success');
        } catch (error) {
            console.error("Error deleting cluster:", error);
            showToast('Failed to delete cluster', 'error');
        } finally {
            setDeletingClusters(prev => {
                const newSet = new Set(prev);
                newSet.delete(stackId);
                return newSet;
            });
        }
    };

    useEffect(() => {
        setCurrentPage(0);
    }, [clusters?.length]);

    const handleRefresh = () => {
        dispatch(fetchClusters());
    };

    const isLoading = status === 'loading';

    const totalClusters = clusters?.length ?? 0;
    const totalPages = Math.ceil(totalClusters / ITEMS_PER_PAGE);
    const paginatedClusters = (clusters ?? []).slice(
        currentPage * ITEMS_PER_PAGE,
        (currentPage + 1) * ITEMS_PER_PAGE
    );

    const renderEmptyClusterState = () => (
        <div className="resource-empty-state">
            <strong>No clusters yet</strong>
            <span>Use a cluster when one application needs more than one virtual machine. Choose an image that starts your application automatically on every member.</span>
            <TrialLock>
                <button type="button" onClick={() => setIsCreateModalOpen(true)}>
                    Create cluster
                </button>
            </TrialLock>
        </div>
    );

    return (
        <div className="keypairs-page">
            <div className="page-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p className="page-subtitle">
                        Manage autoscale clusters
                    </p>
                    <button 
                        className="refresh-button" 
                        onClick={handleRefresh} 
                        disabled={isLoading}
                        style={{
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #e2e8f0',
                            background: 'var(--bg-card)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                        }}
                    >
                        <FaSyncAlt className={isLoading ? 'spin' : ''} />
                        Refresh
                    </button>
                </div>
                <InfoBanner id="clusters-info">
                    <span>A cluster keeps a group of virtual machines behind a shared entry point. It adds or removes members within your chosen range as demand changes; view details to see readiness and members.</span>
                </InfoBanner>
            </div>

            <TrialLock>
                <button
                    className="create-button"
                    onClick={() => setIsCreateModalOpen(true)}
                    title="Create New Cluster"
                >
                    <FaPlus/>
                    <span>Create Cluster</span>
                </button>
            </TrialLock>

            <div className="desktop-view">
                <div className="table-container">
                    <table className="keypairs-table" aria-label="Autoscaling clusters">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Updated</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && clusters?.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="loading-state">
                                        <FaSyncAlt className="spin"/> Loading clusters...
                                    </td>
                                </tr>
                            ) : clusters?.length ? (
                                paginatedClusters.map((cluster) => (
                                    <tr key={cluster.id}>
                                        <td className="keypair-name">{cluster.stack_name}</td>
                                        <td>
                                            {cluster.stack_status === 'CREATE_IN_PROGRESS' ? (
                                                <div>
                                                    <div className="creating-progress"></div>
                                                    <div className="status-label" style={{ color: '#228b22' }}>
                                                        {buildProgressLabel(cluster) ?? 'CREATING'}
                                                    </div>
                                                </div>
                                            ) : cluster.stack_status === 'UPDATE_IN_PROGRESS' ? (
                                                <div>
                                                    <div className="building-progress"></div>
                                                    <div className="status-label" style={{ color: 'var(--status-info)' }}>
                                                        {buildProgressLabel(cluster) ?? 'UPDATING'}
                                                    </div>
                                                </div>
                                            ) : cluster.stack_status === 'DELETE_IN_PROGRESS' || cluster.stack_status === 'ROLLBACK_IN_PROGRESS' || deletingClusters.has(cluster.id) ? (
                                                <div>
                                                    <div className="deleting-progress"></div>
                                                    <div className="status-label" style={{ color: '#ef4444' }}>
                                                        {cluster.stack_status === 'ROLLBACK_IN_PROGRESS' ? 'ROLLING BACK' : 'DELETING'}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className={`status-badge ${(cluster.stack_status ?? 'unknown').toLowerCase().replace(/_/g, '-')}`}>
                                                    {cluster.stack_status ?? 'UNKNOWN'}
                                                </span>
                                            )}
                                        </td>
                                        <td>{formatConsoleDate(cluster.creation_time)}</td>
                                        <td>{cluster.updated_time ? formatConsoleDate(cluster.updated_time) : 'Not available'}</td>
                                        <td>
                                            <Select
                                                options={[
                                                    {value: 'details', label: 'View Details'},
                                                    {value: 'rolling-update', label: 'Rolling Update'},
                                                    {value: 'update', label: 'Update Config'},
                                                    {value: 'delete', label: 'Delete Cluster'}
                                                ]}
                                                onChange={(option) => {
                                                    if (option?.value === 'details') {
                                                        setSelectedCluster(cluster);
                                                        setIsDetailsModalOpen(true);
                                                    } else if (option?.value === 'rolling-update') {
                                                        setSelectedCluster(cluster);
                                                        setIsRollingUpdateModalOpen(true);
                                                    } else if (option?.value === 'update') {
                                                        setSelectedCluster(cluster);
                                                        setIsUpdateModalOpen(true);
                                                    } else if (option?.value === 'delete') {
                                                        handleDeleteCluster(cluster.stack_name, cluster.id);
                                                    }
                                                }}
                                                placeholder="Actions"
                                                isSearchable={false}
                                                aria-label={`Actions for cluster ${cluster.stack_name}`}
                                                isDisabled={deletingClusters.has(cluster.id)}
                                                menuPortalTarget={document.body}
                                                styles={getActionSelectStyles()}
                                            />
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="empty-state">
                                        {renderEmptyClusterState()}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px', borderTop: '1px solid var(--border)' }}>
                            <button
                                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                disabled={currentPage === 0}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                            >← Prev</button>
                            <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalClusters)} of {totalClusters}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={currentPage >= totalPages - 1}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                            >Next →</button>
                        </div>
                    )}
                </div>
            </div>

            <CreateClusterModal
                isOpen={isCreateModalOpen && !creationLocked}
                onClose={() => setIsCreateModalOpen(false)}
                onCreated={() => {
                    dispatch(fetchClusters());
                    setIsCreateModalOpen(false);
                }}
            />

            {selectedCluster && (
                <UpdateClusterModal
                    isOpen={isUpdateModalOpen}
                    onClose={() => {
                        setIsUpdateModalOpen(false);
                        setSelectedCluster(null);
                    }}
                    onUpdated={() => {
                        dispatch(fetchClusters());
                        setIsUpdateModalOpen(false);
                        setSelectedCluster(null);
                    }}
                    cluster={selectedCluster}
                />
            )}

            {selectedCluster && (
                <ClusterDetailsModal
                    isOpen={isDetailsModalOpen}
                    onClose={() => {
                        setIsDetailsModalOpen(false);
                        setSelectedCluster(null);
                    }}
                    cluster={selectedCluster}
                />
            )}

            {selectedCluster && (
                <RollingUpdateModal
                    isOpen={isRollingUpdateModalOpen}
                    onClose={() => {
                        setIsRollingUpdateModalOpen(false);
                        setSelectedCluster(null);
                    }}
                    onStarted={() => {
                        dispatch(fetchClusters());
                    }}
                    cluster={selectedCluster}
                />
            )}
        </div>
    );
};

export default ClustersPage;
